import type { ReactNode, CSSProperties } from 'react';

/**
 * Product-window mockups for the v2 landing pages. HTML and CSS only, drawn from real Kora UI states
 * (ticket list, categories, roles, AI Analyst). All data is example data and the window says so.
 * Colours come from the warm tokens, so dark mode follows. Never screenshots of real customers.
 */

type Data = Record<string, unknown>;
const S = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d);
const A = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

const PRIORITY: Record<string, string> = { Critical: '#C2410C', High: '#D97706', Medium: '#2563EB', Low: '#6B7280' };
const dot = (p: string): CSSProperties => ({ width: 8, height: 8, borderRadius: 99, background: PRIORITY[p] || PRIORITY.Medium, flexShrink: 0 });

export function Chip({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'green' | 'amber' | 'blue' | 'red' }) {
  const t: Record<string, CSSProperties> = {
    neutral: { background: 'var(--warm-inset)', color: 'var(--warm-muted)' },
    green: { background: 'var(--chip-green-bg)', color: 'var(--chip-green)' },
    amber: { background: 'var(--chip-amber-bg)', color: 'var(--chip-amber)' },
    blue: { background: 'var(--chip-blue-bg)', color: 'var(--chip-blue)' },
    red: { background: 'var(--chip-red-bg)', color: 'var(--chip-red)' },
  };
  return <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium leading-4" style={t[tone]}>{children}</span>;
}

/** The window frame: title bar, "Example workspace" label, body. */
export function Window({ title, label = 'Example workspace', children, className = '' }: { title: string; label?: string; children: ReactNode; className?: string }) {
  return (
    <figure
      className={`m-0 overflow-hidden rounded-2xl ${className}`}
      style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)', boxShadow: '0 24px 48px -28px rgba(28,26,21,0.35)' }}
      aria-label={`${title} (${label})`}
    >
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: '1px solid var(--warm-border)', background: 'var(--warm-surface)' }}>
        <span className="flex gap-1.5" aria-hidden="true">
          {[0, 1, 2].map((i) => <span key={i} className="size-2.5 rounded-full" style={{ background: 'var(--warm-handle)' }} />)}
        </span>
        <span className="ml-1 text-xs font-medium" style={{ color: 'var(--warm-text)' }}>{title}</span>
        <span className="ml-auto text-[11px]" style={{ color: 'var(--warm-muted)' }}>{label}</span>
      </div>
      <div className="p-4">{children}</div>
    </figure>
  );
}

const rowStyle: CSSProperties = { background: 'var(--warm-page)', border: '1px solid var(--warm-border)' };

/* ───────────── TicketList ───────────── */
export function TicketList({ data }: { data: Data }) {
  const rows = [
    { id: 14, title: 'Cannot sign in on a new laptop', pr: 'High', st: 'Open', who: 'Emily' },
    { id: 13, title: 'Invoice total does not match the order', pr: 'Medium', st: 'In Progress', who: 'Mike' },
    { id: 12, title: 'Export button missing on the report page', pr: 'Low', st: 'Assigned', who: 'Sarah' },
    { id: 11, title: 'Request: add a due date to tasks', pr: 'Low', st: 'Resolved', who: 'John' },
  ];
  return (
    <Window title={S(data.title, 'Tickets')} label={S(data.label, 'Example workspace')}>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={rowStyle}>
            <span style={dot(r.pr)} title={r.pr} />
            <span className="text-xs tabular-nums" style={{ color: 'var(--warm-muted)' }}>#{r.id}</span>
            <span className="min-w-0 flex-1 truncate text-[13px]" style={{ color: 'var(--warm-text)' }}>{r.title}</span>
            <Chip tone={r.st === 'Resolved' ? 'green' : r.st === 'Open' ? 'amber' : 'blue'}>{r.st}</Chip>
            <span className="hidden text-xs sm:inline" style={{ color: 'var(--warm-muted)' }}>{r.who}</span>
          </li>
        ))}
      </ul>
    </Window>
  );
}

/* ───────────── CategoryQueue ───────────── */
export function CategoryQueue({ data }: { data: Data }) {
  const queues = A(data.queues);
  const counts = [6, 4, 9, 3, 5, 2];
  return (
    <Window title="Queues by category" label={S(data.label, 'Example workspace')}>
      <ul className="space-y-2">
        {queues.map((q, i) => (
          <li key={q} className="rounded-xl px-3 py-2.5" style={rowStyle}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[13px] font-medium" style={{ color: 'var(--warm-text)' }}>{q}</span>
              <span className="text-xs tabular-nums" style={{ color: 'var(--warm-muted)' }}>{counts[i % counts.length]} open</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full" style={{ background: 'var(--warm-inset)' }}>
              <div className="h-full rounded-full" style={{ width: `${30 + ((i * 17) % 55)}%`, background: 'var(--warm-text)', opacity: 0.8 }} />
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-3 flex flex-wrap gap-1.5 text-[11px]" style={{ color: 'var(--warm-muted)' }}>
        <Chip>Claim-based</Chip><Chip>Assignment-based</Chip><Chip>Auto-claim</Chip>
      </p>
    </Window>
  );
}

/* ───────────── RolesTable ───────────── */
const ROLE_USE: Record<string, string> = {
  Owner: 'Company leads. Sets up the workspace and roles',
  Admin: 'Company leads. Adjusts roles and settings',
  Manager: 'Team leads',
  Operations: 'People who raise and follow their own requests',
  Agent: 'People who handle tickets',
  Viewer: 'Read only',
};
export function RolesTable({ data }: { data: Data }) {
  const roles = A(data.roles).length ? A(data.roles) : ['Owner', 'Admin', 'Manager', 'Operations', 'Agent', 'Viewer'];
  return (
    <Window title="Team and roles" label={S(data.label, 'Example workspace')}>
      <ul className="divide-y" style={{ borderColor: 'var(--warm-border)' }}>
        {roles.map((r) => (
          <li key={r} className="flex items-center justify-between gap-3 py-2.5" style={{ borderColor: 'var(--warm-border)' }}>
            <span className="flex items-center gap-2.5">
              <span className="flex size-7 items-center justify-center rounded-full text-[11px] font-semibold" style={{ background: 'var(--warm-inset)', color: 'var(--warm-text)' }}>{r[0]}</span>
              <span className="text-[13px] font-medium" style={{ color: 'var(--warm-text)' }}>{r}</span>
            </span>
            <span className="text-right text-xs" style={{ color: 'var(--warm-muted)' }}>{ROLE_USE[r] ?? ''}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px]" style={{ color: 'var(--warm-muted)' }}>Members are unlimited and free. Typical use shown, not a permissions list.</p>
    </Window>
  );
}

/* ───────────── AnalystChat ───────────── */
export function AnalystChat({ data }: { data: Data }) {
  const rows: [string, number][] = [['IT access', 9], ['Customer question', 7], ['Bug report', 5], ['Payment query', 3]];
  return (
    <Window title="AI Analyst" label={S(data.label, 'Example workspace')}>
      <div className="ml-auto w-fit max-w-[88%] rounded-2xl rounded-br-md px-3.5 py-2 text-[13px]" style={{ background: 'var(--warm-btn)', color: 'var(--warm-btn-text)' }}>
        {S(data.question, 'Which categories have the most open tickets?')}
      </div>
      <div className="mt-3 rounded-2xl rounded-bl-md p-3.5" style={rowStyle}>
        <p className="text-[13px]" style={{ color: 'var(--warm-text)' }}>Open tickets by category right now:</p>
        <ul className="mt-2 space-y-1.5">
          {rows.map(([c, n]) => (
            <li key={c} className="flex items-center gap-2 text-xs" style={{ color: 'var(--warm-muted)' }}>
              <span className="w-32 shrink-0 truncate">{c}</span>
              <span className="h-2 rounded-full" style={{ width: `${n * 9}%`, background: 'var(--warm-text)', opacity: 0.75 }} />
              <span className="tabular-nums" style={{ color: 'var(--warm-text)' }}>{n}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2.5 text-[11px]" style={{ color: 'var(--warm-muted)' }}>1 credit per answer. Example data.</p>
      </div>
    </Window>
  );
}

/* ───────────── TeamsGrid (hero) ───────────── */
export function TeamsGrid({ data }: { data: Data }) {
  const teams = A(data.teams);
  const sample: Record<string, string[]> = {
    Support: ['Billing question from a customer', 'Bug report: upload fails'],
    IT: ['Access request for a new starter', 'Laptop battery replacement'],
    Finance: ['Payment query on an invoice', 'Reimbursement for travel'],
    Product: ['Feature request: dark mode', 'Bug: filter resets on reload'],
  };
  return (
    <Window title="Company workspace" label={S(data.label, 'Example workspace')}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {teams.map((t, i) => (
          <div key={t} className="rounded-xl p-3" style={rowStyle}>
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium" style={{ color: 'var(--warm-text)' }}>{t}</span>
              <Chip>{[5, 3, 4, 6][i % 4]} open</Chip>
            </div>
            <ul className="mt-2 space-y-1.5">
              {(sample[t] ?? ['Request', 'Request']).map((s, k) => (
                <li key={s} className="flex items-center gap-2 text-xs" style={{ color: 'var(--warm-muted)' }}>
                  <span style={dot(k === 0 ? 'High' : 'Medium')} />
                  <span className="truncate">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Window>
  );
}

/* ───────────── SlaTable ───────────── */
export function SlaTable({ data }: { data: Data }) {
  const rows = [['Critical', '1 hour', '1 day'], ['High', '4 hours', '2 days'], ['Medium', '1 day', '5 days'], ['Low', '2 days', '10 days']];
  return (
    <Window title="SLA policy" label={S(data.label, 'Example workspace')}>
      <table className="w-full text-left text-[13px]">
        <thead><tr className="text-xs" style={{ color: 'var(--warm-muted)' }}><th className="pb-2 font-medium">Priority</th><th className="pb-2 font-medium">First response</th><th className="pb-2 font-medium">Resolution</th></tr></thead>
        <tbody>
          {rows.map(([p, a, b]) => (
            <tr key={p} style={{ borderTop: '1px solid var(--warm-border)', color: 'var(--warm-text)' }}>
              <td className="py-2"><span className="inline-flex items-center gap-2"><span style={dot(p)} />{p}</span></td><td className="py-2">{a}</td><td className="py-2">{b}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 flex flex-wrap gap-1.5"><Chip>Clock pauses while waiting</Chip><Chip tone="amber">Breach alert by email and webhook</Chip></p>
    </Window>
  );
}

/* ───────────── TicketDetail ───────────── */
export function TicketDetail({ data }: { data: Data }) {
  return (
    <Window title="Ticket #14" label={S(data.label, 'Example workspace')}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip tone="amber">Open</Chip><Chip>High</Chip><Chip>Access request</Chip>
      </div>
      <p className="mt-2 text-[15px] font-medium" style={{ color: 'var(--warm-text)' }}>Cannot sign in on a new laptop</p>
      <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--warm-muted)' }}>Signed in fine on my old machine. The new laptop shows an error after I enter my email.</p>
      <ul className="mt-3 space-y-2">
        {[['Emily', 'Claimed this ticket and asked for a screenshot.'], ['Mike', 'Added an internal note: check the access group.']].map(([who, what]) => (
          <li key={who} className="flex gap-2.5 rounded-xl px-3 py-2 text-xs" style={rowStyle}>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold" style={{ background: 'var(--warm-inset)', color: 'var(--warm-text)' }}>{who[0]}</span>
            <span style={{ color: 'var(--warm-muted)' }}><strong style={{ color: 'var(--warm-text)' }}>{who}</strong> {what}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px]" style={{ color: 'var(--warm-muted)' }}>Activity log: status changed, owner set, comment added.</p>
    </Window>
  );
}

/* ───────────── StructuringFlow ───────────── */
export function StructuringFlow({ data }: { data: Data }) {
  const magic = S(data.mode) === 'magic-helpdesk';
  const box: CSSProperties = { ...rowStyle, borderRadius: 12, padding: 12 };
  return (
    <Window title={magic ? 'Magic Helpdesk' : 'AI structuring'} label={S(data.label, 'Example workspace')}>
      <div className="grid items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr]">
        <div style={box}>
          <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--warm-muted)' }}>{magic ? 'Your website' : 'Pasted message'}</p>
          <p className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--warm-text)' }}>
            {magic ? 'Reads your site, 9 pages, then plans the articles your customers will look for.' : '“Hi, I was charged twice for last month’s invoice and I need one refunded please. Thanks, Sam”'}
          </p>
        </div>
        <span className="flex items-center justify-center text-lg" style={{ color: 'var(--warm-muted)' }} aria-hidden="true">→</span>
        <div style={box}>
          <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--warm-muted)' }}>{magic ? 'Draft articles' : 'Structured ticket'}</p>
          {magic ? (
            <ul className="mt-2 space-y-1.5 text-xs" style={{ color: 'var(--warm-text)' }}>
              {['Getting started', 'Billing and invoices', 'Resetting your password', 'Managing your team'].map((a) => <li key={a} className="flex items-center justify-between gap-2"><span className="truncate">{a}</span><Chip>Draft</Chip></li>)}
            </ul>
          ) : (
            <dl className="mt-2 space-y-1.5 text-xs">
              {[['Title', 'Double charge on last invoice'], ['Category', 'Billing question'], ['Priority', 'Medium']].map(([k, v]) => <div key={k} className="flex justify-between gap-2"><dt style={{ color: 'var(--warm-muted)' }}>{k}</dt><dd style={{ color: 'var(--warm-text)' }}>{v}</dd></div>)}
            </dl>
          )}
        </div>
      </div>
      <p className="mt-3 text-[11px]" style={{ color: 'var(--warm-muted)' }}>{magic ? 'You review, edit and publish. Nothing goes live on its own.' : 'You review the ticket before it is created.'}</p>
    </Window>
  );
}

/* ───────────── PortalWindow ───────────── */
export function PortalWindow({ data }: { data: Data }) {
  const company = S(data.company, 'Your company');
  return (
    <Window title={`${company} support`} label={S(data.label, 'Example workspace')}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] font-medium" style={{ color: 'var(--warm-text)' }}>Your requests</p>
        <span className="rounded-lg px-3 py-1.5 text-xs font-medium" style={{ background: 'var(--warm-btn)', color: 'var(--warm-btn-text)' }}>New request</span>
      </div>
      <ul className="mt-3 space-y-2">
        {[['Invoice shows the wrong address', 'In Progress'], ['How do I add a second user?', 'Resolved'], ['Export is slow on large files', 'Open']].map(([t, s]) => (
          <li key={t} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-[13px]" style={rowStyle}>
            <span className="truncate" style={{ color: 'var(--warm-text)' }}>{t}</span><Chip tone={s === 'Resolved' ? 'green' : s === 'Open' ? 'amber' : 'blue'}>{s}</Chip>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px]" style={{ color: 'var(--warm-muted)' }}>Customers sign in with an emailed link and see only their own requests.</p>
    </Window>
  );
}

/* ───────────── KoraAiDraft ───────────── */
export function KoraAiDraft({ data }: { data: Data }) {
  if (S(data.mode) === 'agent') {
    return (
      <Window title="Kora AI: resolution agent" label={S(data.label, 'Example workspace')}>
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]" style={{ color: 'var(--warm-muted)' }}>
          Mode: <Chip tone="blue">Auto</Chip><Chip>Approval required</Chip><Chip>Off</Chip>
        </div>
        <div className="mt-3 rounded-xl p-3" style={rowStyle}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium" style={{ color: 'var(--warm-text)' }}>Ticket #21: How do I add a second user?</span>
            <Chip tone="amber">Waiting for approval</Chip>
          </div>
          <p className="mt-2 rounded-lg p-2.5 text-[13px] leading-relaxed" style={{ borderLeft: '3px solid var(--warm-text)', background: 'var(--warm-card)', color: 'var(--warm-text)' }}>
            You can add a second user from Team, then Invite. Members are free, so your bill does not change.
          </p>
          <p className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]" style={{ color: 'var(--warm-muted)' }}>Sources: <Chip tone="blue">Helpdesk article</Chip></p>
          <div className="mt-3 flex gap-2 text-xs font-medium">
            <span className="rounded-lg px-3 py-1.5" style={{ background: 'var(--warm-btn)', color: 'var(--warm-btn-text)' }}>Approve</span>
            <span className="rounded-lg px-3 py-1.5" style={{ border: '1px solid var(--warm-border)', color: 'var(--warm-text)' }}>Edit</span>
            <span className="rounded-lg px-3 py-1.5" style={{ border: '1px solid var(--warm-border)', color: 'var(--warm-text)' }}>Reject</span>
          </div>
        </div>
        <p className="mt-3 text-[11px]" style={{ color: 'var(--warm-muted)' }}>Billing, legal, security and upset customers always go to a person.</p>
      </Window>
    );
  }
  return (
    <Window title="Reply" label={S(data.label, 'Example workspace')}>
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium" style={{ background: 'var(--warm-btn)', color: 'var(--warm-btn-text)' }}>✦ Draft reply</span>
        <span className="text-[11px]" style={{ color: 'var(--warm-muted)' }}>1 credit per draft</span>
      </div>
      <div className="mt-3 rounded-xl p-3 text-[13px] leading-relaxed" style={{ ...rowStyle, borderLeft: '3px solid var(--warm-text)', color: 'var(--warm-text)' }}>
        Hi Sam, thanks for flagging this. You can add a second user from Team, then Invite. Members are free, so there is nothing to change on your bill.
      </div>
      <p className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]" style={{ color: 'var(--warm-muted)' }}>
        Sources: <Chip tone="blue">Helpdesk article</Chip><Chip>Knowledge base</Chip>
      </p>
      <p className="mt-2 flex gap-3 text-[11px]" style={{ color: 'var(--warm-muted)' }}><span>Regenerate</span><span>Not helpful</span><span>You edit and send</span></p>
    </Window>
  );
}

/* ───────────── CodeWindow ───────────── */
export function CodeWindow({ data }: { data: Data }) {
  const headers = A(data.headers).length ? A(data.headers) : ['X-Workspace-ID', 'X-Secret-Key'];
  const line = (t: string, key: string) => <div key={key} className="whitespace-pre">{t}</div>;
  return (
    <Window title="Public API" label={S(data.label, 'Example workspace')}>
      <pre className="m-0 overflow-x-auto rounded-xl p-3 text-[12px] leading-6" style={{ ...rowStyle, color: 'var(--warm-text)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
        {line('curl https://api.usekora.app/public/v1/tickets \\', 'a')}
        {headers.map((h, i) => line(`  -H "${h}: <your ${h === 'X-Workspace-ID' ? 'workspace ID' : 'secret key'}>"${i < headers.length - 1 ? ' \\' : ''}`, h))}
      </pre>
      <pre className="m-0 mt-2 overflow-x-auto rounded-xl p-3 text-[12px] leading-6" style={{ ...rowStyle, color: 'var(--warm-muted)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
        {line('{ "success": true,', 'r1')}{line('  "tickets": [ { "id": 14, "title": "…", "status": "Open" } ] }', 'r2')}
      </pre>
    </Window>
  );
}

/* ───────────── WebhookLog ───────────── */
export function WebhookLog({ data }: { data: Data }) {
  const rows: [string, string, string][] = [['ticket.created', '200', 'just now'], ['ticket.assigned', '200', '2 min ago'], ['ticket.sla_breach', 'Retry scheduled', '5 min ago'], ['ticket.resolved', '200', '9 min ago']];
  return (
    <Window title="Webhook deliveries" label={S(data.label, 'Example workspace')}>
      <ul className="space-y-2">
        {rows.map(([ev, st, t]) => (
          <li key={ev} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px]" style={rowStyle}>
            <code className="flex-1 truncate text-xs" style={{ color: 'var(--warm-text)' }}>{ev}</code>
            <Chip tone={st === '200' ? 'green' : 'amber'}>{st}</Chip>
            <span className="hidden text-xs sm:inline" style={{ color: 'var(--warm-muted)' }}>{t}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 flex flex-wrap gap-1.5"><Chip>Signed with HMAC-SHA256</Chip><Chip>Logs kept 30 days</Chip></p>
    </Window>
  );
}

/* ───────────── SessionEmbed ───────────── */
export function SessionEmbed({ data }: { data: Data }) {
  return (
    <Window title="Your product" label={S(data.label, 'Example workspace')}>
      <div className="grid gap-3 sm:grid-cols-[96px_1fr]">
        <ul className="hidden space-y-1.5 text-xs sm:block" style={{ color: 'var(--warm-muted)' }}>{['Dashboard', 'Orders', 'Support', 'Settings'].map((n) => <li key={n} className="rounded-lg px-2 py-1.5" style={n === 'Support' ? { background: 'var(--warm-inset)', color: 'var(--warm-text)' } : {}}>{n}</li>)}</ul>
        <div className="rounded-xl p-3" style={{ ...rowStyle, borderStyle: 'dashed' }}>
          <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--warm-muted)' }}>Kora, opened for a signed-in member</p>
          <ul className="mt-2 space-y-1.5">
            {['Cannot sign in on a new laptop', 'Invoice total does not match'].map((t, i) => <li key={t} className="flex items-center gap-2 text-xs" style={{ color: 'var(--warm-text)' }}><span style={dot(i ? 'Medium' : 'High')} />{t}</li>)}
          </ul>
        </div>
      </div>
    </Window>
  );
}

/* ───────────── CostBars ───────────── */
export function CostBars({ data }: { data: Data }) {
  // mockupData.items like "Draft reply $0.10": bar width follows the price.
  const parsed = A(data.items).map((t) => { const m = t.match(/^(.*?)\s*\$([0-9.]+)$/); return m ? ([m[1], Number(m[2])] as [string, number]) : null; }).filter((x): x is [string, number] => !!x);
  if (parsed.length) {
    const max = Math.max(...parsed.map((p) => p[1]));
    return (
      <Window title="Kora AI prices" label="Calculation, not a customer">
        <ul className="space-y-3">
          {parsed.map(([l, v]) => (
            <li key={l}>
              <div className="flex justify-between text-xs" style={{ color: 'var(--warm-muted)' }}><span>{l}</span><span className="tabular-nums" style={{ color: 'var(--warm-text)' }}>${v.toFixed(2)}</span></div>
              <div className="mt-1 h-2 overflow-hidden rounded-full" style={{ background: 'var(--warm-inset)' }}><div className="h-full rounded-full" style={{ width: `${(v / max) * 100}%`, background: 'var(--warm-text)', opacity: 0.8 }} /></div>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[11px]" style={{ color: 'var(--warm-muted)' }}>No draft, no charge. Escalations and failures are free.</p>
      </Window>
    );
  }
  const rows: [string, number][] = [['100 tickets', 10], ['500 tickets', 50], ['1,000 tickets', 100]];
  return (
    <Window title="Cost at $0.10 per ticket" label="Calculation, not a customer">
      <ul className="space-y-3">
        {rows.map(([l, v]) => (
          <li key={l}>
            <div className="flex justify-between text-xs" style={{ color: 'var(--warm-muted)' }}><span>{l}</span><span className="tabular-nums" style={{ color: 'var(--warm-text)' }}>${v}</span></div>
            <div className="mt-1 h-2 overflow-hidden rounded-full" style={{ background: 'var(--warm-inset)' }}><div className="h-full rounded-full" style={{ width: `${v}%`, background: 'var(--warm-text)', opacity: 0.8 }} /></div>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px]" style={{ color: 'var(--warm-muted)' }}>Members are unlimited and free, so the bill follows tickets only.</p>
    </Window>
  );
}

/* ───────────── Registry ───────────── */
const REGISTRY: Record<string, (p: { data: Data }) => JSX.Element> = {
  TicketList, TicketDetail, StructuringFlow, AnalystChat, SlaTable, PortalWindow, KoraAiDraft,
  CodeWindow, RolesTable, CategoryQueue, WebhookLog, SessionEmbed, CostBars, TeamsGrid,
};

export function Mockup({ id, data = {} }: { id: string; data?: Data }) {
  const C = REGISTRY[id];
  if (!C) throw new Error(`Unknown mockup ${id}`);
  return <C data={data} />;
}

/** Small thumbnails for the "how it works" steps: a few lines of the same UI, not a screenshot. */
export function Thumb({ id }: { id: string }) {
  const bar = (w: string, o = 0.18) => <span className="block h-1.5 rounded-full" style={{ width: w, background: 'var(--warm-text)', opacity: o }} />;
  return (
    <div className="flex h-16 flex-col justify-center gap-1.5 rounded-xl px-3" style={rowStyle} aria-hidden="true">
      {id === 'RolesTable' ? (
        <div className="flex flex-wrap gap-1">{['O', 'A', 'M', 'Op', 'Ag', 'V'].map((r) => <span key={r} className="flex size-5 items-center justify-center rounded-full text-[9px] font-semibold" style={{ background: 'var(--warm-inset)', color: 'var(--warm-text)' }}>{r}</span>)}</div>
      ) : id === 'CategoryQueue' ? (
        <>{bar('80%', 0.5)}{bar('55%', 0.35)}{bar('35%', 0.25)}</>
      ) : (
        <>
          {[0, 1, 2].map((i) => (
            <span key={i} className="flex items-center gap-1.5"><span style={{ ...dot(i === 0 ? 'High' : 'Medium'), width: 6, height: 6 }} />{bar(['78%', '60%', '70%'][i], 0.22)}</span>
          ))}
        </>
      )}
    </div>
  );
}
